import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import { createSeededNoise } from "../../utils/perlinNoise";

export type CharacterSet =
    | "letters"
    | "letters-punctuation"
    | "letters-numbers"
    | "everything"
    | "all-caps-letters"
    | "all-caps-letters-numbers"
    | "all-caps-letters-punctuation";

const CHARACTER_SETS: Record<CharacterSet, string> = {
    letters: "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz",
    "letters-punctuation":
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz.,!?;:'\"-()[]{}",
    "letters-numbers":
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789",
    everything:
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789.,!?;:'\"-()[]{}@#$%^&*+=<>/\\|`~",
    "all-caps-letters": "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
    "all-caps-letters-numbers": "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
    "all-caps-letters-punctuation":
        "ABCDEFGHIJKLMNOPQRSTUVWXYZ.,!?;:'\"-()[]{}",
};

export interface TextCharacter {
    x: number;
    y: number;
    char: string;
}

interface TextNoiseFillState {
    font: string;
    lineHeight: number;
    fontSize: number;
    characterSpacing: number;
    characterSet: CharacterSet;
    noiseScale: number;
    density: number;
    minGroupLength: number;
    seed: string;
    setFont: (font: string) => void;
    setLineHeight: (lh: number) => void;
    setFontSize: (fs: number) => void;
    setCharacterSpacing: (cs: number) => void;
    setCharacterSet: (cs: CharacterSet) => void;
    setNoiseScale: (scale: number) => void;
    setDensity: (density: number) => void;
    setMinGroupLength: (length: number) => void;
    setSeed: (seed: string) => void;
    viewBoxAttr: string;
    textCharacters: TextCharacter[];
    canExport: boolean;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const TextNoiseFillContext = createContext<TextNoiseFillState | null>(null);

export function useTextNoiseFill() {
    const context = useContext(TextNoiseFillContext);
    if (!context) {
        throw new Error(
            "useTextNoiseFill must be used within TextNoiseFillProvider"
        );
    }
    return context;
}

interface TextNoiseFillProviderProps {
    children: React.ReactNode;
    initialFont?: string;
    initialLineHeight?: number;
    initialFontSize?: number;
    initialCharacterSpacing?: number;
    initialCharacterSet?: CharacterSet;
    initialNoiseScale?: number;
    initialDensity?: number;
    initialMinGroupLength?: number;
    initialSeed?: string;
}

export function TextNoiseFillProvider({
    children,
    initialFont = "monospace",
    initialLineHeight = 20,
    initialFontSize = 14,
    initialCharacterSpacing = 8,
    initialCharacterSet = "letters",
    initialNoiseScale = 0.1,
    initialDensity = 0.4,
    initialMinGroupLength = 1,
    initialSeed = "",
}: TextNoiseFillProviderProps) {
    const [font, setFont] = useState(initialFont);
    const [lineHeight, setLineHeight] = useState(initialLineHeight);
    const [fontSize, setFontSize] = useState(initialFontSize);
    const [characterSpacing, setCharacterSpacing] = useState(
        initialCharacterSpacing
    );
    const [characterSet, setCharacterSet] =
        useState<CharacterSet>(initialCharacterSet);
    const [noiseScale, setNoiseScale] = useState(initialNoiseScale);
    const [density, setDensity] = useState(initialDensity);
    const [minGroupLength, setMinGroupLength] = useState(initialMinGroupLength);
    const [seed, setSeed] = useState(initialSeed);

    const viewBoxAttr = "0 0 1000 1000";

    const textCharacters = useMemo(() => {
        const viewBoxParts = viewBoxAttr.split(/\s+/).map(Number);
        if (viewBoxParts.length !== 4) return [];

        const [x, y, width, height] = viewBoxParts;
        const characters: TextCharacter[] = [];

        // Create seeded noise function
        const seededNoise = createSeededNoise(seed || "default");

        // Use Perlin noise to determine characters
        const chars = CHARACTER_SETS[characterSet];
        const charsLength = chars.length;

        let currentY = y;
        while (currentY < y + height) {
            let currentX = x;

            while (currentX < x + width) {
                // Use Perlin noise to determine if character should be placed
                // Lower noise values = spaces (darker areas)
                // Higher noise values = characters (lighter areas)
                const noiseValue = seededNoise(
                    currentX * noiseScale,
                    currentY * noiseScale
                );

                // Threshold: only place characters where noise is above a certain value
                // This creates patterns where dark areas (low noise) become spaces
                // Higher density = more characters (lower threshold)
                // Lower density = fewer characters (higher threshold)
                // Inverted: use (1 - density) as threshold so higher density = more characters

                if (noiseValue > 1 - density) {
                    // Determine group length using noise (minimum + variation)
                    const groupLengthNoise = seededNoise(
                        currentX * noiseScale + 2000,
                        currentY * noiseScale + 2000
                    );
                    // Group length ranges from minGroupLength to minGroupLength + 5
                    // Map noise (0-1) to additional length (0-5)
                    // Use Math.ceil with offset to ensure we can get 0, or clamp to ensure minimum
                    const additionalLength = Math.min(
                        5,
                        Math.floor(groupLengthNoise * 6)
                    );
                    const groupLength = minGroupLength + additionalLength;

                    // Get a unique offset for this group to ensure character variety
                    // Use the group's starting position with a large offset to differentiate groups
                    const groupOffsetX =
                        seededNoise(
                            currentX * noiseScale + 5000,
                            currentY * noiseScale + 5000
                        ) * 10000;
                    const groupOffsetY =
                        seededNoise(
                            currentX * noiseScale + 6000,
                            currentY * noiseScale + 6000
                        ) * 10000;

                    // Create a group of characters
                    for (
                        let i = 0;
                        i < groupLength && currentX < x + width;
                        i++
                    ) {
                        // Use a separate noise sample for character selection
                        // Add group-specific offsets and character index to ensure variety
                        // This ensures each group and each character within a group is unique
                        const charNoiseValue = seededNoise(
                            currentX * noiseScale +
                                1000 +
                                groupOffsetX +
                                i * 500,
                            currentY * noiseScale +
                                1000 +
                                groupOffsetY +
                                i * 300
                        );
                        const charIndex = Math.floor(
                            charNoiseValue * charsLength
                        );
                        const char = chars[charIndex] || chars[0];

                        characters.push({
                            x: currentX,
                            y: currentY + fontSize, // Adjust for baseline
                            char: char,
                        });

                        currentX += characterSpacing;
                    }
                } else {
                    // If noise is below threshold, we skip (space) - no character added
                    currentX += characterSpacing;
                }
            }

            currentY += lineHeight;
        }

        return characters;
    }, [
        viewBoxAttr,
        characterSet,
        lineHeight,
        characterSpacing,
        fontSize,
        noiseScale,
        density,
        minGroupLength,
        seed,
    ]);

    const canExport = textCharacters.length > 0 && !!viewBoxAttr;

    const buildExportSvgString = () => {
        if (!textCharacters.length) return "";

        const textElements = textCharacters
            .map((char) => {
                return `    <text x="${char.x}" y="${char.y}" font-family="${font}" font-size="${fontSize}" fill="black">${char.char}</text>`;
            })
            .join("\n");

        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
  <g class="text-fill">
${textElements}
  </g>
</svg>`;
    };

    const handleDownload = () => {
        if (!canExport) return;
        const svgString = buildExportSvgString();
        if (!svgString) return;

        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `text-noise-fill-${Date.now()}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            font, lineHeight, fontSize, characterSpacing, characterSet,
            noiseScale, density, minGroupLength, seed,
        }),
        [
            font, lineHeight, fontSize, characterSpacing, characterSet,
            noiseScale, density, minGroupLength, seed,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.font != null) setFont(values.font as string);
        if (values.lineHeight != null) setLineHeight(values.lineHeight as number);
        if (values.fontSize != null) setFontSize(values.fontSize as number);
        if (values.characterSpacing != null) setCharacterSpacing(values.characterSpacing as number);
        if (values.characterSet != null) setCharacterSet(values.characterSet as CharacterSet);
        if (values.noiseScale != null) setNoiseScale(values.noiseScale as number);
        if (values.density != null) setDensity(values.density as number);
        if (values.minGroupLength != null) setMinGroupLength(values.minGroupLength as number);
        if (values.seed != null) setSeed(values.seed as string);
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "text-noise-fill",
        defaultPreset: {
            font: "monospace",
            lineHeight: 20,
            fontSize: 14,
            characterSpacing: 8,
            characterSet: "letters" as CharacterSet,
            noiseScale: 0.1,
            density: 0.4,
            minGroupLength: 1,
            seed: "",
        },
        getPresetValues,
        applyPreset,
    });

    const value: TextNoiseFillState = {
        font,
        lineHeight,
        fontSize,
        characterSpacing,
        characterSet,
        noiseScale,
        density,
        minGroupLength,
        seed,
        setFont,
        setLineHeight,
        setFontSize,
        setCharacterSpacing,
        setCharacterSet,
        setNoiseScale,
        setDensity,
        setMinGroupLength,
        setSeed,
        viewBoxAttr,
        textCharacters,
        canExport,
        buildExportSvgString,
        handleDownload,
        applyPreset,
        getPresetValues,
        resetParameters,
    };

    return (
        <TextNoiseFillContext.Provider value={value}>
            {children}
        </TextNoiseFillContext.Provider>
    );
}
